import { CellContext, RowData } from "@tanstack/react-table";
import { Em, IconButton, Text } from "@radix-ui/themes";
import { DateFormatter } from "@/v2/components/formatters/DateFormatter";
import { NumberFormatter } from "@/v2/components/formatters/NumberFormatter";
import { RatioFormatter } from "@/v2/components/formatters/RationFormatter";
import { DurationFormatter } from "@/v2/components/formatters/DurationFromatter";
import { StatusFormatter } from "@/v2/components/formatters/StatusFormatter";
import { ChannelFormatter } from "@/v2/components/formatters/ChannelFormatter";
import { TopicFormatter } from "@/v2/components/formatters/TopicFormatter";
import { CategoryFormatter } from "@/v2/components/formatters/CategoryFormatter";
import { AvgQaScoreFormatter } from "@/v2/components/formatters/AvgQaScoreFormatter";
import { AvgQuackMessageScoreFormatter } from "@/v2/components/formatters/AvgQuackMessageScoreFormatter";
import { CSATFormatter } from "@/v2/components/formatters/CSATFormatter";
import { Scorecard } from "@/v2/entities/explore/Explore.model";
import { RowActions } from "./SimpleTable";
import { DropdownMenu } from "@/components/shared/dropdown-menu";
import { DotsVerticalIcon } from "@radix-ui/react-icons";

export type CellRenderer<RowData, CellData> = (
  args: CellContext<RowData, CellData>
) => JSX.Element;

export function placeholderCell<RowDataType extends RowData>({
  args,
  cellRender,
  placeholder = "Unclassified",
}: {
  args: CellContext<RowDataType, unknown>;
  cellRender: CellRenderer<RowDataType, unknown>;
  placeholder?: string;
}) {
  const value = args.getValue<unknown | undefined>();

  if (value == null || value === "") {
    return (
      <Text size="2" color="gray" weight={"medium"}>
        <Em>{placeholder}</Em>
      </Text>
    );
  }

  return cellRender(args);
}

export function defaultCellRenderer<RowDataType extends RowData>({
  getValue,
}: CellContext<RowDataType, unknown>) {
  const value = getValue<number | null>();

  if (value == null) {
    return <></>;
  }

  return <>{value}</>;
}

export function titleCell<RowDataType extends RowData>({
  getValue,
}: CellContext<RowDataType, unknown>) {
  const value = getValue<string | undefined>();

  return (
    <Text size={"2"} weight={"medium"} highContrast>
      {value ?? "-"}
    </Text>
  );
}

export function groupByTitleCell<RowDataType extends RowData>({
  getValue,
}: CellContext<RowDataType, unknown>) {
  const value = getValue<string>();

  return (
    <Text size={"2"} weight={"medium"} highContrast>
      {value}
    </Text>
  );
}

export function dateTimeCell<RowDataType extends RowData>({
  getValue,
}: CellContext<RowDataType, unknown>) {
  const value = getValue<string | undefined>();

  if (value == null) {
    return <></>;
  }

  return <DateFormatter value={value} />;
}

export function numberCell<RowDataType extends RowData>({
  getValue,
}: CellContext<RowDataType, unknown>) {
  const value = getValue<number | undefined>();

  if (value == null) {
    return <></>;
  }

  return <NumberFormatter value={value} />;
}

export function csatCell<RowDataType extends RowData>({
  getValue,
}: CellContext<RowDataType, unknown>) {
  const value = getValue<number | undefined>();

  if (value == null) {
    return <></>;
  }

  return <CSATFormatter value={value} />;
}

export function ratioCell<RowDataType extends RowData>({
  getValue,
}: CellContext<RowDataType, unknown>) {
  const value = getValue<number | undefined>();

  if (value == null) {
    return <></>;
  }

  return <RatioFormatter value={value} />;
}

export function minuteDurationCell<RowDataType extends RowData>({
  getValue,
}: CellContext<RowDataType, unknown>) {
  const value = getValue<number | undefined>();

  if (value == null) {
    return <></>;
  }

  return <DurationFormatter value={value} />;
}

export function booleanCell<RowDataType extends RowData>({
  getValue,
}: CellContext<RowDataType, unknown>) {
  const value = getValue<boolean | undefined>();

  if (value == null) {
    return <></>;
  }

  return (
    <Text size="1" weight={"medium"} highContrast>
      {value ? "Yes" : "No"}
    </Text>
  );
}

export function statusCell<RowDataType extends RowData>({
  getValue,
}: CellContext<RowDataType, unknown>) {
  const value = getValue<string | undefined>();

  if (value == null) {
    return <></>;
  }

  return <StatusFormatter value={value} />;
}

export function channelCell<RowDataType extends RowData>({
  getValue,
}: CellContext<RowDataType, unknown>) {
  const value = getValue<string | undefined>();

  if (value == null) {
    return <></>;
  }

  return <ChannelFormatter value={value} />;
}

export function topicCell<RowDataType extends RowData>({
  getValue,
}: CellContext<RowDataType, unknown>) {
  const value = getValue<string | undefined>();

  if (value == null) {
    return <></>;
  }

  return <TopicFormatter value={value} />;
}

export function categoryCell<RowDataType extends RowData>({
  getValue,
}: CellContext<RowDataType, unknown>) {
  const value = getValue<string | undefined>();

  if (value == null) {
    return <></>;
  }

  return <CategoryFormatter value={value} />;
}

export function groupByCategoryCell<RowDataType extends RowData>({
  getValue,
}: CellContext<RowDataType, unknown>) {
  const value = getValue<string | undefined>();

  return (
    <Text size={"2"} weight={"medium"} highContrast>
      {value ? <CategoryFormatter value={value} /> : "Unclassified"}
    </Text>
  );
}

export function avgQaScoreCell<RowDataType extends RowData>({
  getValue,
  scorecards,
}: CellContext<RowDataType, unknown> & {
  scorecards?: Scorecard[];
}) {
  const value = getValue<number | string | undefined>();

  if (value == null) {
    return <>-</>;
  }

  return <AvgQaScoreFormatter value={+value} scorecards={scorecards} />;
}

export function avgAutoQaScoreCell<RowDataType extends RowData>({
  getValue,
  scorecards,
}: CellContext<RowDataType, unknown> & {
  scorecards?: Scorecard[];
}) {
  const value = getValue<number | string | undefined>();

  if (value == null) {
    return <>-</>;
  }

  return <AvgQaScoreFormatter value={+value} scorecards={scorecards} />;
}

export function groupByAvgQaScoreRoundRangeCell<RowDataType extends RowData>({
  getValue,
}: CellContext<RowDataType, unknown>) {
  const value = getValue<number | undefined>();

  if (value == null) {
    return <></>;
  }

  return (
    <Text size={"2"} weight={"medium"} highContrast>
      <AvgQaScoreFormatter value={value} />
    </Text>
  );
}

export function groupByAvgQuackMessageScoreRoundRangeCell<
  RowDataType extends RowData,
>({ getValue }: CellContext<RowDataType, unknown>) {
  const value = getValue<number | undefined>();

  if (value == null) {
    return <></>;
  }

  return (
    <Text size={"2"} weight={"medium"} highContrast>
      <AvgQuackMessageScoreFormatter value={value} />
    </Text>
  );
}

export function actionsCell<RowDataType extends RowData>({
  table: {
    options: { meta },
  },
  row,
}: CellContext<RowDataType, unknown>) {
  const actions = meta?.getRowActions?.(row.original) ?? meta?.rowActions;

  if (actions == null) {
    return <></>;
  }

  return (
    <DropdownMenu
      side="bottom"
      align="end"
      items={actions.map((action) => ({
        label: action.label,
        icon: action.icon,
        onClick: (e) => {
          e.stopPropagation();
          action.onClick(row.original);
        },
      }))}
    >
      <DropdownMenu.Trigger>
        <IconButton variant="soft" size="1" color="gray" highContrast>
          <DotsVerticalIcon />
        </IconButton>
      </DropdownMenu.Trigger>
    </DropdownMenu>
  );
}
